/**
 * findingFingerprint — a cheap semantic key for a sanity-check finding,
 * used by the Apply-on-Rerun pack (D5b) to hard-filter findings the
 * user already chose to "ignore" so a rerun can't re-surface the same
 * gap under slightly reworded text.
 *
 * The fingerprint is deliberately coarse: lowercase → strip diacritics
 * → drop punctuation → drop stopwords → take the first N significant
 * words, sorted so word order doesn't matter. Two findings that are
 * "the same thing said differently" collapse to the same key; two
 * genuinely different findings don't. It's a recall-biased filter —
 * we'd rather drop a near-duplicate than re-nag the user — but the
 * sort + N-word cap keeps false collapses rare.
 *
 * Optionally scoped by category/section when those carry through (they
 * do for `PrevSanityResolution.applyDirective.section`, less so for the
 * bare `finding` string the ignore path forwards) — callers that have
 * the scope pass it; callers that don't get the text-only key.
 */

/** Stopwords dropped before fingerprinting — generic English glue plus
 *  a few report-domain fillers that carry no discriminating signal. */
const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'of', 'to', 'in', 'on', 'at', 'for', 'by',
  'with', 'from', 'as', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'this',
  'that', 'these', 'those', 'it', 'its', 'has', 'have', 'had', 'not', 'no', 'than',
  'then', 'so', 'such', 'which', 'who', 'whom', 'what', 'when', 'where', 'why', 'how',
  'about', 'into', 'over', 'under', 'between', 'data', 'report', 'section', 'finding',
  'value', 'values', 'appears', 'appear', 'may', 'might', 'could', 'should', 'would',
  'site', 'sites',
]);

const SIGNIFICANT_WORD_COUNT = 8;

/** Lowercase, strip diacritics, drop non-alphanumerics → token list. */
function tokenize(text: string): string[] {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // combining diacritical marks
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Build the fingerprint for a finding's text. `scope` (e.g. category or
 * section) is prefixed when supplied so a "missing X" in the Grid
 * section and the same words in the Climate section don't collide.
 */
export function fingerprintFinding(findingText: string, scope?: { category?: string; section?: string }): string {
  const significant = tokenize(findingText)
    .filter(t => t.length > 2 && !STOPWORDS.has(t))
    .slice(0, SIGNIFICANT_WORD_COUNT)
    .sort();
  const base = significant.join(' ');
  const cat = scope?.category ? scope.category.trim().toLowerCase() : '';
  const sec = scope?.section ? tokenize(scope.section).filter(t => !STOPWORDS.has(t)).sort().join(' ') : '';
  const prefixParts = [cat, sec].filter(Boolean);
  return prefixParts.length > 0 ? `${prefixParts.join('|')}::${base}` : base;
}

/**
 * Given the set of fingerprints the user has ignored, drop any new
 * finding whose fingerprint matches. Findings carrying their own
 * `section` are matched both scoped-by-section AND text-only (the
 * ignore path only forwards the bare `finding` string, so an ignored
 * key is text-only — a new finding must collapse to the same text key
 * to be dropped).
 */
export function filterIgnoredFindings<T extends { finding: string; category?: string; section?: string }>(
  newFindings: T[],
  ignoredFingerprints: ReadonlySet<string>,
): T[] {
  if (ignoredFingerprints.size === 0) return newFindings;
  return newFindings.filter(f => {
    const textKey = fingerprintFinding(f.finding);
    if (ignoredFingerprints.has(textKey)) return false;
    const scopedKey = fingerprintFinding(f.finding, { category: f.category, section: f.section });
    if (ignoredFingerprints.has(scopedKey)) return false;
    return true;
  });
}

/** Build the ignored-fingerprint set from prior resolutions. The ignore
 *  path forwards only the bare `finding` string, so we key text-only;
 *  if a future caller forwards section/category too, pass it through. */
export function buildIgnoredFingerprintSet(
  ignored: ReadonlyArray<{ finding: string; category?: string; section?: string }>,
): Set<string> {
  const set = new Set<string>();
  for (const r of ignored) {
    set.add(fingerprintFinding(r.finding));
    if (r.category || r.section) set.add(fingerprintFinding(r.finding, { category: r.category, section: r.section }));
  }
  return set;
}
